import { createContext, useContext, useState, useEffect } from 'react'
import { authAPI } from '../services/api'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [justLoggedIn, setJustLoggedIn] = useState(false)

  useEffect(() => {
    // Get CSRF token first, then check auth
    const initializeAuth = async () => {
      try {
        // Fetch CSRF token to ensure it's available
        await authAPI.getCsrfToken()
      } catch (error) {
        console.warn('Could not fetch CSRF token:', error)
      }
      // Only check auth if we haven't just logged in (to avoid clearing user state)
      if (!justLoggedIn) {
        checkAuth()
      } else {
        setLoading(false)
      }
    }
    initializeAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkAuth = async () => {
    try {
      const response = await authAPI.getCurrentUser()
      console.log('checkAuth response:', response.data)
      setUser(response.data)
      setJustLoggedIn(false) // Reset flag after successful auth check
    } catch (error) {
      console.log('checkAuth failed:', error.response?.status, error.message)
      // NEVER clear user if we just logged in (cookies might not be set yet)
      // This prevents redirect loop after successful login
      if (!justLoggedIn && !user) {
        setUser(null)
      } else if (!justLoggedIn) {
        // Only clear if we have a user but checkAuth fails and we didn't just log in
        console.warn('checkAuth failed but keeping existing user state')
      }
    } finally {
      setLoading(false)
    }
  }

  const login = async (username, password) => {
    try {
      // First, get CSRF token if not already available
      const csrfToken = getCookie('csrftoken')
      if (!csrfToken) {
        try {
          await authAPI.getCsrfToken()
        } catch (csrfError) {
          console.warn('Could not fetch CSRF token:', csrfError)
        }
      }

      // Now attempt login
      const response = await authAPI.login(username, password)
      console.log('Login response:', response.data)
      
      // EMERGENCY FIX: Store user from login response immediately
      // Don't rely on checkAuth - it may fail if cookies aren't set yet
      if (response.data.success) {
        // Set flag to prevent checkAuth from clearing user state
        setJustLoggedIn(true)
        
        // CRITICAL: Store user from login response immediately
        if (response.data.user) {
          console.log('✅ Login successful, storing user:', response.data.user)
          setUser(response.data.user)
          setLoading(false)
          
          // Verify session in background (non-blocking)
          setTimeout(() => {
            checkAuth().then(() => {
              console.log('✅ Session verified successfully')
              setJustLoggedIn(false)
            }).catch(err => {
              console.warn('⚠️ Session verification failed, but keeping user state:', err)
              // Keep user state even if checkAuth fails
              setJustLoggedIn(false)
            })
          }, 2000)
          
          return { success: true, user: response.data.user }
        }
        
        // If no user in response, try to get it (shouldn't happen)
        console.warn('⚠️ No user data in login response, attempting checkAuth')
        await new Promise(resolve => setTimeout(resolve, 1000))
        try {
          await checkAuth()
          setJustLoggedIn(false)
          return { success: true }
        } catch (authError) {
          console.error('❌ Failed to get user after login:', authError)
          // Still return success - login succeeded
          setJustLoggedIn(false)
          return { success: true }
        }
      }
      return { success: false, error: response.data.message || 'Login failed' }
    } catch (error) {
      console.error('Login error:', error)
      // Handle CSRF errors specifically
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('CSRF')) {
        return {
          success: false,
          error: 'CSRF validation failed. Please refresh the page and try again.',
        }
      }
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Login failed',
      }
    }
  }

  // Helper function to get cookie
  function getCookie(name) {
    let cookieValue = null
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';')
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim()
        if (cookie.substring(0, name.length + 1) === name + '=') {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
          break
        }
      }
    }
    return cookieValue
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      window.location.href = '/login'
    }
  }

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isStaff: user?.is_staff || false,
    isSuperuser: user?.is_superuser || false,
    isClientuser: user?.category === 'clientuser' || false,
    isAdmin: user?.is_superuser || false,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

